/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx } from '@emotion/react';
import React, { useMemo, useState } from 'react';
import { BaseInputProps } from '../shared-types';
import { getClassNames, getGetIdFn, hex2rgba, toPascalCase } from '../utils';
import BaseCheckbox from './BaseCheckbox';
import Typography from '../Typography/Typography';
import useTheme from '../style/useTheme';

export type Props = Omit<BaseInputProps, 'onChange' | 'label'> &
  React.PropsWithChildren<{
    // Make `label` non-optional
    label: string;
    checked?: boolean;
    onChange: (value: boolean) => void;
  }>;

const CheckboxField: React.FC<Props> = ({
  label,
  checked,
  onChange,
  disabled,
  ariaLabel,
  children,
}) => {
  const { theme, resolveColor } = useTheme();
  const fieldId = useMemo(
    () => getGetIdFn('CheckboxField')(toPascalCase(label)),
    [label]
  );

  const checkboxId = useMemo(
    () => getGetIdFn('Checkbox')(toPascalCase(label)),
    [label]
  );

  const [focused, setFocused] = useState(false);

  return (
    <div
      onMouseOver={() => setFocused(true)}
      onFocus={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
      css={{
        padding: '16px',
        display: 'flex',
        borderRadius: '8px',
        transition: `all ${theme.animations.duration.standard}ms ${theme.animations.easing.easeOut}`,
        flexDirection: 'column',
        alignItems: 'flex-start',
        cursor: 'pointer',
        '&:hover': {
          background: hex2rgba(
            resolveColor(theme.components.checkbox['selected-background']),
            0.04
          ),
        },
        backgroundColor: checked
          ? hex2rgba(
              resolveColor(theme.components.checkbox['selected-background']),
              0.08
            )
          : 'transparent',
      }}
      data-testid={fieldId}
      className={getClassNames(
        { 'origami-Radio--selected': checked },
        'origami-Radio'
      )}
      onClick={() => {
        if (!disabled && onChange) {
          onChange(!checked);
        }
      }}
    >
      <BaseCheckbox
        id={checkboxId}
        focused={focused}
        checked={!!checked}
        disabled={disabled}
        ariaLabel={ariaLabel}
      >
        <Typography variant="body">{label}</Typography>
      </BaseCheckbox>
      {children}
    </div>
  );
};

export default CheckboxField;
